import React, { useState } from "react";
import { CompleteBattleLog } from "types/battle-engine";

interface MenuScreenProps {
  onStartBattle: (battleLog: CompleteBattleLog) => void;
  isAppReady: boolean;
  isLocalTest?: boolean;
  battleLog?: CompleteBattleLog | null;
}

export const MenuScreen: React.FC<MenuScreenProps> = ({
  onStartBattle,
  isAppReady,
  isLocalTest = false,
  battleLog: externalBattleLog,
}) => {
  const [battleLog, setBattleLog] = useState<CompleteBattleLog | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Use external battle log if provided (for local test mode)
  const effectiveBattleLog = externalBattleLog || battleLog;

  const handleLoadBattleLog = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setBattleLog(JSON.parse(e.target?.result as string));
    };
    reader.readAsText(file);
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleLoadBattleLog(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    console.log("file", file);
    if (file && file.name.endsWith(".json")) handleLoadBattleLog(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const canStartBattle = effectiveBattleLog !== null;

  if (isLocalTest) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center p-8 z-[9999]">
        <h1 className="text-5xl font-bold text-white mb-8 drop-shadow-lg">
          Local Test Mode
        </h1>

        <div className="text-center mb-8">
          <p className="text-xl text-gray-300 mb-2">
            Battle log loaded automatically
          </p>
          <p className="text-sm text-gray-400">
            Ready to start the battle visualization
          </p>
        </div>

        <button
          disabled={!isAppReady}
          onClick={() => onStartBattle(effectiveBattleLog!)}
          className={`px-8 py-4 font-bold rounded-xl transition-all ${
            isAppReady
              ? "bg-green-600 hover:bg-green-700 text-white shadow-lg hover:scale-105"
              : "bg-gray-600 text-gray-400 cursor-not-allowed"
          }`}
        >
          {!isAppReady ? "Initializing..." : "Start Battle"}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`absolute inset-0 flex flex-col items-center justify-center p-8 z-[9999] transition ${
        isDragging ? "bg-purple-900/40" : ""
      }`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
    >
      <h1 className="text-5xl font-bold text-white mb-8 drop-shadow-lg">
        Battle Setup
      </h1>

      <label
        className={`px-6 py-3 font-semibold rounded-lg cursor-pointer transition-all ${
          isAppReady
            ? "bg-purple-600 hover:bg-purple-700 text-white shadow-md"
            : "bg-gray-600 text-gray-400 cursor-not-allowed"
        }`}
      >
        <input
          type="file"
          accept=".json"
          onChange={handleFileInput}
          className="hidden"
        />
        Drop or Select JSON File
      </label>

      <div className="flex flex-row gap-3 mt-6">
        <button
          disabled={!canStartBattle}
          onClick={() => onStartBattle(effectiveBattleLog!)}
          className={`px-8 py-4 font-bold rounded-xl transition-all ${
            canStartBattle
              ? "bg-green-600 hover:bg-green-700 text-white shadow-lg hover:scale-105"
              : "bg-gray-600 text-gray-400 cursor-not-allowed"
          }`}
        >
          {!isAppReady
            ? "Initializing..."
            : canStartBattle
            ? "Start"
            : "Select Pokes"}
        </button>
      </div>
    </div>
  );
};
